import React, { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataToolbar, StatusBadge } from "@/components/data/Common";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

export default function Breakdowns() {
  const [list, setList] = useState([]);
  const [eqs, setEqs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ project_id: "", equipment_id: "", date: today, description: "", root_cause: "", corrective_action: "", downtime_hours: 0, manpower: 1, cost: 0, status: "open" });

  useEffect(() => {
    api.get("/projects").then((r) => { setProjects(r.data); if (r.data[0]) setForm((f) => ({ ...f, project_id: r.data[0].id })); });
    api.get("/equipment").then((r) => setEqs(r.data));
    load();
  }, []);
  const load = () => api.get("/breakdowns").then((r) => setList(r.data));

  const submit = async () => {
    try { await api.post("/breakdowns", form); toast.success("Breakdown logged"); setOpen(false); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const eqMap = Object.fromEntries(eqs.map((e) => [e.id, e]));
  const filt = list.filter((b) => !q || `${b.description} ${b.root_cause}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <AppLayout title="Breakdowns" subtitle="Unplanned downtime events and corrective actions">
      <DataToolbar search={q} setSearch={setQ} onAdd={() => setOpen(true)} addLabel="Log Breakdown"
        addTestId="bd-add-button" searchTestId="bd-search-input" />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead><TableHead>Equipment</TableHead><TableHead>Description</TableHead>
              <TableHead>Root Cause</TableHead><TableHead className="text-right">Downtime (h)</TableHead>
              <TableHead className="text-right">Cost (₹)</TableHead><TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filt.map((b) => (
              <TableRow key={b.id} data-testid={`bd-row-${b.id}`}>
                <TableCell className="font-mono text-xs">{b.date}</TableCell>
                <TableCell className="font-mono text-xs">{eqMap[b.equipment_id]?.code || "—"}</TableCell>
                <TableCell className="max-w-md truncate">{b.description}</TableCell>
                <TableCell className="text-slate-500 text-sm">{b.root_cause || "—"}</TableCell>
                <TableCell className="text-right font-mono">{b.downtime_hours}</TableCell>
                <TableCell className="text-right font-mono">{b.cost?.toLocaleString("en-IN")}</TableCell>
                <TableCell><StatusBadge status={b.status} /></TableCell>
              </TableRow>
            ))}
            {!filt.length && <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">No breakdowns</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="bd-create-dialog">
          <DialogHeader><DialogTitle>Log Breakdown</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-cap">Date</Label><Input type="date" data-testid="bd-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div>
              <Label className="label-cap">Equipment</Label>
              <Select value={form.equipment_id} onValueChange={(v) => setForm({ ...form, equipment_id: v })}>
                <SelectTrigger data-testid="bd-equipment-input"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{eqs.map((e) => <SelectItem key={e.id} value={e.id}>{e.code} · {e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="label-cap">Description</Label><Textarea data-testid="bd-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label className="label-cap">Root Cause</Label><Input data-testid="bd-rootcause-input" value={form.root_cause} onChange={(e) => setForm({ ...form, root_cause: e.target.value })} /></div>
            <div><Label className="label-cap">Corrective Action</Label><Input data-testid="bd-action-input" value={form.corrective_action} onChange={(e) => setForm({ ...form, corrective_action: e.target.value })} /></div>
            <div><Label className="label-cap">Downtime (hours)</Label><Input type="number" step="0.1" data-testid="bd-downtime-input" value={form.downtime_hours} onChange={(e) => setForm({ ...form, downtime_hours: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label className="label-cap">Manpower</Label><Input type="number" data-testid="bd-manpower-input" value={form.manpower} onChange={(e) => setForm({ ...form, manpower: parseInt(e.target.value) || 0 })} /></div>
            <div><Label className="label-cap">Cost (₹)</Label><Input type="number" data-testid="bd-cost-input" value={form.cost} onChange={(e) => setForm({ ...form, cost: parseFloat(e.target.value) || 0 })} /></div>
          </div>
          <DialogFooter><Button onClick={submit} className="bg-sky-900 hover:bg-sky-950" data-testid="bd-submit-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
